import { StateAbbreviation, TIER_II_MANAGER_STATES } from "../states";
import { getT2mLepcIdMapByState } from "../t2m-lepc-fd-id-map/getByState";
import { normalizeCountyName } from "../../utils/county";

/**
 * T2M states that don't have a Fire Department selector in their portal.
 * - MA: State submits to FDs directly
 * - PA: No FD dropdown in portal
 */
export const TIER_II_MANAGER_STATES_WITHOUT_FD_SELECTOR: readonly string[] = [
  "MA",
  "PA",
];

/**
 * T2M states that don't have an LEPC selector in their portal.
 * - MA: No LEPC dropdown
 * - MN: No LEPC dropdown
 */
export const TIER_II_MANAGER_STATES_WITHOUT_LEPC_SELECTOR: readonly string[] = [
  "MA",
  "MN",
];

/**
 * Check if a T2M state has a Fire Department selector in its portal.
 */
export function t2mStateHasFdSelector(state: string): boolean {
  return (
    isT2mState(state) &&
    !TIER_II_MANAGER_STATES_WITHOUT_FD_SELECTOR.includes(state)
  );
}

/**
 * Check if a T2M state has an LEPC selector in its portal.
 */
export function t2mStateHasLepcSelector(state: string): boolean {
  return (
    isT2mState(state) &&
    !TIER_II_MANAGER_STATES_WITHOUT_LEPC_SELECTOR.includes(state)
  );
}

import AR from "./AR.json";
import CT from "./CT.json";
import DE from "./DE.json";
import IL from "./IL.json";
import IN from "./IN.json";
import KY from "./KY.json";
import MA from "./MA.json";
import MD from "./MD.json";
import MI from "./MI.json";
import MN from "./MN.json";
import ND from "./ND.json";
import NV from "./NV.json";
import OR from "./OR.json";
import PA from "./PA.json";
import WI from "./WI.json";
import WV from "./WV.json";

type T2MCountyLepcFdItem = {
  value: string;
  display: string;
};

// Some states (like WI) have agencies nested by municipality within county
type T2MAgencyData =
  | T2MCountyLepcFdItem[]
  | Record<string, T2MCountyLepcFdItem[]>;

type T2MCountyLepcFdMap = {
  counties: T2MCountyLepcFdItem[];
  LEPCs: {
    [key: string]: T2MAgencyData;
  };
  FDs: {
    [key: string]: T2MAgencyData;
  };
  municipalities: {
    [key: string]: T2MCountyLepcFdItem[];
  };
};

/**
 * Check if a state is a T2M state that has county dropdown mappings.
 */
export function isT2mState(
  state: string
): state is (typeof TIER_II_MANAGER_STATES)[number] {
  return (TIER_II_MANAGER_STATES as readonly string[]).includes(state);
}

/**
 * Match a geocoded county name to the correct T2M county display value.
 *
 * This function takes a geocoded county name (e.g., "Rock Island County" from AWS)
 * and finds the matching T2M county display value from the state's dropdown options.
 *
 * Different T2M states have different display formats:
 * - IL: "Rock Island  " (no "County", trailing spaces)
 * - IN: "St. Joseph" (no "County")
 * - WI: "Rock County(10062)" (has "County" suffix + code in parens)
 * - KY: "Jefferson County" (has "County" suffix)
 *
 * @param geocodedCounty - The county name from geocoding (e.g., "Rock Island County")
 * @param state - The state abbreviation (must be a T2M state)
 * @returns The matching T2M county display value, or undefined if no match found
 */
export function matchT2mCounty(
  geocodedCounty: string,
  state: StateAbbreviation
): string | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const normalizedGeocoded = normalizeCountyName(geocodedCounty);

  // Find the T2M county whose normalized display value matches the normalized geocoded county
  const match = t2mData.counties.find((county) => {
    const normalizedT2m = normalizeCountyName(county.display);
    return normalizedT2m === normalizedGeocoded;
  });

  return match?.display;
}

/**
 * Get all T2M county display values for a state.
 *
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of T2M county display values, or undefined if not a T2M state
 */
export function getT2mCountyDisplayValues(
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  return t2mData.counties.map((county) => county.display);
}

export function getT2mCountyLepcFdByState(
  state: StateAbbreviation
): T2MCountyLepcFdMap {
  switch (state) {
    case "AR":
      return AR;
    case "CT":
      return CT;
    case "DE":
      return DE;
    case "IL":
      return IL;
    case "IN":
      return IN;
    case "KY":
      return KY;
    case "MA":
      return MA;
    case "MD":
      return MD;
    case "MI":
      return MI;
    case "MN":
      return MN;
    case "ND":
      return ND;
    case "NV":
      return NV;
    case "OR":
      return OR;
    case "PA":
      return PA;
    case "WI":
      // The file is too big and causes TS type checking to fail.
      // @ts-ignore
      return WI as T2MCountyLepcFdMap;
    case "WV":
      return WV;
    default:
      throw new Error(`No T2M LEPC ID map for state ${state}`);
  }
}

/**
 * Find the T2M county key that matches a given county display value.
 * The T2M JSON files use county names as keys for LEPCs and FDs mappings,
 * which may have trailing spaces or other formatting quirks.
 *
 * @param countyDisplay - The county display value to find a key for
 * @param state - The state abbreviation (must be a T2M state)
 * @returns The T2M county key, or undefined if no match found
 */
function findT2mCountyKey(
  countyDisplay: string,
  state: StateAbbreviation
): string | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const normalizedInput = normalizeCountyName(countyDisplay);

  // First, check if there's an exact key match (LEPCs/FDs use county display as key)
  // Note: Some states use the county name as key without trailing spaces
  for (const key of Object.keys(t2mData.LEPCs)) {
    if (normalizeCountyName(key) === normalizedInput) {
      return key;
    }
  }

  // Also check FDs keys in case they differ
  for (const key of Object.keys(t2mData.FDs)) {
    if (normalizeCountyName(key) === normalizedInput) {
      return key;
    }
  }

  return undefined;
}

/**
 * Get the valid LEPCs for a county in a T2M state.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid LEPC display values for the county, or undefined if not a T2M state
 */
export function getValidLepcsForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const lepcsData = t2mData.LEPCs[countyKey];
  return flattenAgencyData(lepcsData).map((lepc) => lepc.display);
}

/**
 * Get the valid FD display names for a county in a T2M state.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid FD display values for the county, or undefined if not a T2M state
 */
export function getValidFdsForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const fdsData = t2mData.FDs[countyKey];
  return flattenAgencyData(fdsData).map((fd) => fd.display);
}

/**
 * Flatten nested agency data structure.
 * Some states (like WI) have agencies nested by municipality within county.
 * This function handles both flat arrays and nested objects.
 *
 * @param data - Either an array of items or an object with arrays as values
 * @returns Flattened array of items
 */
function flattenAgencyData(
  data:
    | T2MCountyLepcFdItem[]
    | Record<string, T2MCountyLepcFdItem[]>
    | undefined
): T2MCountyLepcFdItem[] {
  if (!data) {
    return [];
  }

  // If it's already an array, return it
  if (Array.isArray(data)) {
    return data;
  }

  // If it's a nested object (like WI's FDs by municipality), flatten all values
  return Object.values(data).flat();
}

/**
 * Get the valid FD T2M portal values (IDs) for a county in a T2M state.
 *
 * These values correspond to the `value` field in the T2M JSON and should
 * match the `portalId` field on our FireDepartment records.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid T2M FD values for the county, or undefined if not a T2M state
 */
export function getValidFdT2mValuesForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const fdsData = t2mData.FDs[countyKey];
  return flattenAgencyData(fdsData).map((fd) => fd.value);
}

/**
 * Get the valid FD DDB IDs for a county in a T2M state.
 *
 * This function:
 * 1. Gets valid T2M values from the county dropdown mapping
 * 2. Maps those T2M values to DDB IDs using the T2M-to-DDB mapping
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid FD DDB IDs for the county, or undefined if not a T2M state
 */
export function getValidFdIdsForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  const t2mValues = getValidFdT2mValuesForCounty(county, state);
  if (t2mValues === undefined) {
    return undefined;
  }

  // Map T2M values to DDB IDs
  const idMap = getT2mLepcIdMapByState(state);
  const t2mToDdbMap = new Map(idMap.fire.map((f) => [f.t2mValue, f.ddbId]));

  return t2mValues
    .map((t2mValue) => t2mToDdbMap.get(t2mValue))
    .filter((id): id is string => id !== undefined);
}

/**
 * Get the valid LEPC T2M portal values (IDs) for a county in a T2M state.
 *
 * These values correspond to the `value` field in the T2M JSON and should
 * match the `portalId` field on our Lepc records.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid T2M LEPC values for the county, or undefined if not a T2M state
 */
export function getValidLepcT2mValuesForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const lepcsData = t2mData.LEPCs[countyKey];
  return flattenAgencyData(lepcsData).map((lepc) => lepc.value);
}

/**
 * Get the valid LEPC DDB IDs for a county in a T2M state.
 *
 * This function:
 * 1. Gets valid T2M values from the county dropdown mapping
 * 2. Maps those T2M values to DDB IDs using the T2M-to-DDB mapping
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid LEPC DDB IDs for the county, or undefined if not a T2M state
 */
export function getValidLepcIdsForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  const t2mValues = getValidLepcT2mValuesForCounty(county, state);
  if (t2mValues === undefined) {
    return undefined;
  }

  // Map T2M values to DDB IDs
  const idMap = getT2mLepcIdMapByState(state);
  const t2mToDdbMap = new Map(idMap.lepc.map((l) => [l.t2mValue, l.ddbId]));

  return t2mValues
    .map((t2mValue) => t2mToDdbMap.get(t2mValue))
    .filter((id): id is string => id !== undefined);
}
